import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";

// "2026-10-29T20:15" (datetime-local, browser time) <-> ISO with zone
const toLocalInput = (iso) => {
  const d = new Date(iso || "");
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (value) => (value ? new Date(value).toISOString() : null);

// Edit / Schedule / Delete for a draft in the X queue (status "draft").
// Drafts are never posted; Schedule moves one to the queue (status "pending").
// textEditable=false: only the time can change (used where the text shown is a
// truncated copy, e.g. a Calendar planner event).
export default function XDraftActions({ draft, onChanged, compact = false, textEditable = true }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(draft.text || "");
  const [when, setWhen] = useState(toLocalInput(draft.scheduled_at));
  const [busy, setBusy] = useState(false);
  const size = compact ? "text-[10px]" : "text-[11px]";
  const btn = `${size} font-bold uppercase tracking-wider hover:underline disabled:opacity-40`;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); setEditing(false); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const save = () => run(() => api.patch(`/api/x/queue/${draft.id}`, {
    ...(textEditable ? { text } : {}), scheduled_at: fromLocalInput(when) || undefined,
  }), "Draft saved");
  const schedule = () => {
    const iso = fromLocalInput(when);
    if (!iso || new Date(iso) <= new Date()) { setEditing(true); toast.error("Pick a future time to schedule this draft"); return; }
    return run(async () => {
      if (textEditable && editing && text !== draft.text) await api.patch(`/api/x/queue/${draft.id}`, { text });
      await api.post(`/api/x/queue/${draft.id}/schedule`, { scheduled_at: iso });
    }, "Draft scheduled");
  };
  const remove = () => {
    if (!window.confirm("Delete this draft?")) return;
    return run(() => api.delete(`/api/x/queue/${draft.id}/draft`), "Draft deleted");
  };

  return (
    <div className="mt-2 space-y-2" data-testid={`x-draft-actions-${draft.id}`}>
      {draft.source && <div className={`${size} font-mono-ui`} style={{ color: "var(--gio-muted-2)" }}>
        Draft · {draft.source}{draft.quote_tweet_id ? ` · quotes ${draft.quote_tweet_id} (posted as plain text)` : ""}
      </div>}
      {editing && (
        <div className="space-y-2">
          {textEditable && <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={280}
            aria-label="Draft text" data-testid={`x-draft-text-${draft.id}`}
            className="w-full rounded border p-2 text-[13px]" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)", color: "var(--gio-text)" }} />}
          <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
            aria-label="Draft time" data-testid={`x-draft-when-${draft.id}`}
            className="w-full rounded border p-1.5 text-[12px]" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)", color: "var(--gio-text)" }} />
          {textEditable && <div className={`${size}`} style={{ color: "var(--gio-muted-3)" }}>{text.length}/280</div>}
        </div>
      )}
      <div className="flex gap-3">
        {editing
          ? <button type="button" onClick={save} disabled={busy || !text.trim()} data-testid={`x-draft-save-${draft.id}`} className={btn} style={{ color: "var(--gio-primary)" }}>SAVE</button>
          : <button type="button" onClick={() => setEditing(true)} disabled={busy} data-testid={`x-draft-edit-${draft.id}`} className={btn} style={{ color: "var(--gio-primary)" }}>EDIT</button>}
        <button type="button" onClick={schedule} disabled={busy || !text.trim()} data-testid={`x-draft-schedule-${draft.id}`} className={btn} style={{ color: "#FB923C" }}>SCHEDULE</button>
        <button type="button" onClick={remove} disabled={busy} data-testid={`x-draft-delete-${draft.id}`} className={`${btn} ml-auto`} style={{ color: "var(--gio-muted-2)" }}>DELETE</button>
      </div>
    </div>
  );
}
